// Map / navigation links for the Location accordions. Every link starts
// turn-by-turn driving navigation to the site, in the native app where possible.
//
// The plain https universal links (waze.com/ul, google.com/maps) only reach the
// native app when the browser honours Android App Links. Embedded WebViews
// (Teams, Outlook, Gmail) never do, and a `target="_blank"` navigation out of the
// installed PWA lands in a Custom Tab where the handoff is unreliable too — so
// users kept getting the web page instead of the app.
//
// On Android we therefore hand out an `intent:` URL naming the package directly
// (waze:// for Waze, google.navigation: for Maps). That skips App Link
// verification entirely, and browser_fallback_url still covers the "app not
// installed" case. iOS and desktop keep the https link.

const WAZE_PACKAGE = 'com.waze';
const GOOGLE_MAPS_PACKAGE = 'com.google.android.apps.maps';
const GOOGLE_NAV_SCHEME = 'google.navigation';

const isAndroid = () => /android/i.test(navigator.userAgent);

const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent)
  // iPadOS 13+ reports itself as a Mac, so fall back to touch support.
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const isMobile = () => isAndroid() || isIOS();

// `intent:<uri>#Intent;...;end` — parsed by Chrome, Edge, Samsung Internet and
// Firefox on Android. `uri` is everything before the fragment; `scheme` is
// prepended to it to form the URI actually handed to the package.
function androidIntent(uri, scheme, pkg, fallbackUrl) {
  return `intent:${uri}#Intent;scheme=${scheme};package=${pkg};`
    + `S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end`;
}

// Empty/missing address or coordinates would otherwise navigate to
// `...query=undefined`, so render a dead button instead.
const isBlank = value => value === null || value === undefined || `${value}`.trim() === '';

/**
 * `target` for a map link: a new tab on desktop so the user doesn't lose the
 * app, but same-tab on mobile — a top-level, user-gesture navigation is the form
 * Android hands off to a native app most reliably. Returning null drops the
 * attribute when bound with `:target`.
 */
export function mapLinkTarget() {
  return isMobile() ? null : '_blank';
}

// Waze's ll= parses coordinates only. A free-text address has to go
// through q=, or Waze opens with no destination set at all.
function wazeUrl(query) {
  const webUrl = `https://waze.com/ul?${query}`;
  return isAndroid()
    ? androidIntent(`//?${query}`, 'waze', WAZE_PACKAGE, webUrl)
    : webUrl;
}

export function wazeAddressUrl(address) {
  if (isBlank(address)) return null;
  return wazeUrl(`q=${encodeURIComponent(address)}&navigate=yes`);
}

export function wazeCoordinatesUrl(coordinates) {
  if (isBlank(coordinates)) return null;
  return wazeUrl(`ll=${encodeURIComponent(coordinates)}&navigate=yes`);
}

// Google Maps' equivalent of Waze's navigate=yes. On Android, google.navigation:
// starts turn-by-turn immediately; the https dir URL is the fallback for devices
// without Maps, and the href everywhere else. Both accept a free-text address or
// "lat,lng" alike, so Google needs no ll=/q= split the way Waze does.
//
// google.navigation: is an opaque URI — `q=…` goes in with no `//` prefix.
function googleMapsUrl(destination) {
  const encoded = encodeURIComponent(destination);
  const webUrl = `https://www.google.com/maps/dir/?api=1&destination=${encoded}`
    // Explicit, so a stale walking/transit preference can't ride along: these
    // are service vehicles.
    + '&travelmode=driving&dir_action=navigate';
  return isAndroid()
    ? androidIntent(`q=${encoded}`, GOOGLE_NAV_SCHEME, GOOGLE_MAPS_PACKAGE, webUrl)
    : webUrl;
}

export function googleMapsAddressUrl(address) {
  if (isBlank(address)) return null;
  return googleMapsUrl(address);
}

export function googleMapsCoordinatesUrl(coordinates) {
  if (isBlank(coordinates)) return null;
  return googleMapsUrl(coordinates);
}
